import { useTranslation } from "react-i18next";

import { ArrowBack } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Button, Typography, Link, styled } from "@mui/material";
import { auth } from "marvin-auth-kit";
import { Field, Form, Formik, FormikHelpers } from "formik";

import * as Yup from "yup";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useConfig } from "../../hooks/settingHooks";
import { useErrorSnackbar } from "../../hooks/errorHooks";
import { NavLink } from "../../components/navigation/NavLink";
import { TextField } from "../../components/form/TextField";

interface Props {}

interface Credentials {
  username: string;
  password: string;
}

export function LoginPage(props: Props) {
  const { t } = useTranslation();
  const { _env_ } = window;
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const config = useConfig();
  const { enqueueErrorSnackbar } = useErrorSnackbar();
  const initialValues: Credentials = { username: "", password: "" };
  const validationScheme = Yup.object().shape({
    username: Yup.string().required(
      t("validation.requiredField", { field: t("label.userName") })
    ),
    password: Yup.string().required(
      t("validation.requiredField", { field: t("label.password") })
    ),
  });

  async function onSubmit(
    values: Credentials,
    helpers: FormikHelpers<Credentials>
  ) {
    const { setSubmitting } = helpers;
    const { username, password } = values;
    setSubmitting(true);
    try {
      await auth.login(username, password);
      const reffer = decodeURIComponent(
        params.get("reffer") || `/${config}/app/layers`
      );
      navigate(reffer);
    } catch (errors) {
      enqueueErrorSnackbar(errors);
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationScheme}
      onSubmit={onSubmit}
    >
      {({ isSubmitting }) => (
        <StyledForm>
          <Button
            startIcon={<ArrowBack />}
            component={NavLink}
            to={decodeURIComponent(
              params.get("reffer") || `/${config}/app/layers`
            )}
            variant="text"
            className="back-button"
          >
            {t("label.backToApp")}
          </Button>
          <Typography variant="h3" component="h3" color="textSecondary">
            {t("label.welcome")}
          </Typography>
          <Typography
            variant="subtitle1"
            component="h4"
            color="textSecondary"
            className="subtitle"
          >
            {t("label.loginToContinue")}
          </Typography>
          <Field
            component={TextField}
            autoFocus
            className="login-field"
            name="username"
            type="text"
            label={t("label.userName")}
            disabled={isSubmitting}
          />
          <Field
            component={TextField}
            className="login-field"
            name="password"
            label={t("label.password")}
            type="password"
            disabled={isSubmitting}
          />
          <div className="button-group">
            <LoadingButton
              className="submit"
              variant="contained"
              loading={isSubmitting}
              type="submit"
            >
              {t("label.login")}
            </LoadingButton>
            <Button
              component={NavLink}
              to={`/${config}/app/password/forgot`}
              variant="text"
              disabled={isSubmitting}
            >
              {t("label.forgotPassword")}
            </Button>
            {_env_ && _env_.REACT_APP_ALLOW_REGISTRATION && (
              <Link
                component={NavLink}
                className="right-align"
                to={`/${config}/app/register`}
              >
                {t("label.register")}
              </Link>
            )}
          </div>
        </StyledForm>
      )}
    </Formik>
  );
}

const StyledForm = styled(Form)`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;

  .button-group {
    display: flex;
    align-items: center;

    > * + * {
      margin-left: ${({ theme }) => `${theme.spacing(1)}`};
    }
  }

  .login-field,
  .subtitle,
  .button-group {
    margin-bottom: ${({ theme }) => `${theme.spacing(3)}`};
  }

  .right-align {
    margin-left: auto;
  }

  .back-button {
    color: #fff;
    position: absolute;
    top: ${({ theme }) => `${theme.spacing(-8)}`};
    right: 0;
  }
`;
